/**
 * Бухгалтерия (админка). Контракт — docs/worklog/2026-11-04-admin-accounting.md.
 * Все пути требуют ADMIN + право ACCOUNTING. Месяц — "YYYY-MM" (МСК), ставки — доли (0.06 = 6 %), share участника — вес (части).
 */

import api from './axios';

export const ACCOUNTING_PERMISSION = 'ACCOUNTING';

const BASE = '/api/admin/accounting';

export interface AccountingParticipantRef {
  id: string;
  name: string;
  share: number;
  fraction: number;
  active: boolean;
}

export interface AccountingPayout {
  participantId: string;
  share: number;
  reimbursement: number;
  total: number;
  withdrawn: number;
  balance: number;
}

export interface AccountingWithdrawal {
  id: string;
  participantId: string;
  paidOn: string;
  month: string;
  amount: number;
  note: string | null;
  createdAt: string;
}

export interface AccountingWithdrawalRequest {
  participantId: string;
  paidOn: string;
  amount: number;
  note?: string;
}

export interface AccountingIncomeLine {
  sourceCode: string;
  title: string;
  store: boolean;
  orders: number;
  amount: number;
  commission: number;
}

export interface AccountingMonth {
  month: string;
  incomes: AccountingIncomeLine[];
  gross: number;
  commission: number;
  tax: number;
  expenses: number;
  net: number;
  payouts: AccountingPayout[];
}

export interface AccountingTotals {
  gross: number;
  commission: number;
  tax: number;
  expenses: number;
  net: number;
  payouts: AccountingPayout[];
}

export interface AccountingSummary {
  taxRate: number;
  participants: AccountingParticipantRef[];
  months: AccountingMonth[];
  totals: AccountingTotals;
  sharesSum: number;
}

export interface AccountingPeriod {
  from?: string;
  to?: string;
}

export interface AccountingExpense {
  id: string;
  spentOn: string;
  month: string;
  category: string;
  description: string | null;
  amount: number;
  paidByParticipantId: string | null;
  receiptUrl: string | null;
  createdAt: string;
}

export interface AccountingExpenseRequest {
  spentOn: string;
  category: string;
  description?: string;
  amount: number;
  paidByParticipantId?: string;
  receiptUrl?: string;
}

export interface AccountingIncome {
  id: string;
  month: string;
  sourceCode: string;
  amount: number;
  note: string | null;
  createdAt: string;
}

export interface AccountingIncomeRequest {
  month: string;
  sourceCode: string;
  amount: number;
  note?: string;
}

export interface AccountingSource {
  code: string;
  title: string;
  commissionRate: number;
  storeProvider: boolean;
  note: string | null;
  position: number;
}

export interface AccountingSourceCreateRequest {
  code: string;
  title: string;
  commissionRate: number;
  note?: string;
}

export interface AccountingSourceUpdateRequest {
  title: string;
  commissionRate: number;
  note?: string;
}

export interface AccountingParticipant {
  id: string;
  name: string;
  share: number;
  position: number;
  active: boolean;
}

export interface AccountingParticipantCreateRequest {
  name: string;
  share: number;
}

export interface AccountingParticipantUpdateRequest {
  name: string;
  share: number;
  active: boolean;
}

export const getAccountingSummary = (period: AccountingPeriod = {}): Promise<AccountingSummary> =>
  api.get<AccountingSummary>(`${BASE}/summary`, { params: period }).then(r => r.data);

export const listAccountingExpenses = (period: AccountingPeriod = {}): Promise<AccountingExpense[]> =>
  api.get<AccountingExpense[]>(`${BASE}/expenses`, { params: period }).then(r => r.data);

export const createAccountingExpense = (request: AccountingExpenseRequest): Promise<AccountingExpense> =>
  api.post<AccountingExpense>(`${BASE}/expenses`, request).then(r => r.data);

export const updateAccountingExpense = (id: string, request: AccountingExpenseRequest): Promise<AccountingExpense> =>
  api.put<AccountingExpense>(`${BASE}/expenses/${encodeURIComponent(id)}`, request).then(r => r.data);

export const deleteAccountingExpense = (id: string): Promise<void> =>
  api.delete(`${BASE}/expenses/${encodeURIComponent(id)}`).then(() => undefined);

export const listAccountingWithdrawals = (period: AccountingPeriod = {}): Promise<AccountingWithdrawal[]> =>
  api.get<AccountingWithdrawal[]>(`${BASE}/withdrawals`, { params: period }).then(r => r.data);

export const createAccountingWithdrawal = (request: AccountingWithdrawalRequest): Promise<AccountingWithdrawal> =>
  api.post<AccountingWithdrawal>(`${BASE}/withdrawals`, request).then(r => r.data);

export const updateAccountingWithdrawal = (id: string, request: AccountingWithdrawalRequest): Promise<AccountingWithdrawal> =>
  api.put<AccountingWithdrawal>(`${BASE}/withdrawals/${encodeURIComponent(id)}`, request).then(r => r.data);

export const deleteAccountingWithdrawal = (id: string): Promise<void> =>
  api.delete(`${BASE}/withdrawals/${encodeURIComponent(id)}`).then(() => undefined);

export const listAccountingCategories = (): Promise<string[]> =>
  api.get<string[]>(`${BASE}/categories`).then(r => r.data);

export const listAccountingIncomes = (period: AccountingPeriod = {}): Promise<AccountingIncome[]> =>
  api.get<AccountingIncome[]>(`${BASE}/incomes`, { params: period }).then(r => r.data);

export const createAccountingIncome = (request: AccountingIncomeRequest): Promise<AccountingIncome> =>
  api.post<AccountingIncome>(`${BASE}/incomes`, request).then(r => r.data);

export const updateAccountingIncome = (id: string, request: AccountingIncomeRequest): Promise<AccountingIncome> =>
  api.put<AccountingIncome>(`${BASE}/incomes/${encodeURIComponent(id)}`, request).then(r => r.data);

export const deleteAccountingIncome = (id: string): Promise<void> =>
  api.delete(`${BASE}/incomes/${encodeURIComponent(id)}`).then(() => undefined);

export const listAccountingSources = (): Promise<AccountingSource[]> =>
  api.get<AccountingSource[]>(`${BASE}/sources`).then(r => r.data);

export const createAccountingSource = (request: AccountingSourceCreateRequest): Promise<AccountingSource> =>
  api.post<AccountingSource>(`${BASE}/sources`, request).then(r => r.data);

export const updateAccountingSource = (code: string, request: AccountingSourceUpdateRequest): Promise<AccountingSource> =>
  api.put<AccountingSource>(`${BASE}/sources/${encodeURIComponent(code)}`, request).then(r => r.data);

export const listAccountingParticipants = (): Promise<AccountingParticipant[]> =>
  api.get<AccountingParticipant[]>(`${BASE}/participants`).then(r => r.data);

export const createAccountingParticipant = (request: AccountingParticipantCreateRequest): Promise<AccountingParticipant> =>
  api.post<AccountingParticipant>(`${BASE}/participants`, request).then(r => r.data);

export const updateAccountingParticipant = (
  id: string,
  request: AccountingParticipantUpdateRequest,
): Promise<AccountingParticipant> =>
  api.put<AccountingParticipant>(`${BASE}/participants/${encodeURIComponent(id)}`, request).then(r => r.data);

export const getAccountingTaxRate = (): Promise<number> =>
  api.get<{ taxRate: number }>(`${BASE}/config`).then(r => r.data.taxRate);

export const updateAccountingTaxRate = (taxRate: number): Promise<number> =>
  api.put<{ taxRate: number }>(`${BASE}/config`, { taxRate }).then(r => r.data.taxRate);
